// Incoming attacks in the player's other worlds (Ben, Oct 15 2026): the World tab glows red while any world the player
// isn't in right now has incoming attacks, and the world sheet marks each world under attack with its count.
// The back end has no account-wide incoming call; each world's village/get carries that world's whole city list
// (target_char.villages, with num_incoming_attacks), so one read per world — the same read and cache key as the Portal's
// (usePortal: the world's first city) — gives the world total. Polled every 60 s while the app is in the foreground.
import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { api } from "@/api/client";
import { keys, ownVillages, presenceVillageId, presenceVillages, useInit } from "./queries";
import { useSession } from "./session";
import { worldAttack, type WorldAttack } from "@/game/incoming";

export type { WorldAttack };

/** `skip`: the world whose total the caller already has (the current world, from the header's own city) — not read here. */
export function useWorldIncoming(skip: number | null): Map<number, WorldAttack> {
  const init = useInit();
  const uid = useSession((s) => s.uid);
  const worlds = init.data?.world;
  const targets = useMemo(
    () =>
      (worlds?.worlds_in ?? [])
        .filter((wid) => wid !== skip)
        .map((wid) => ({ wid, vid: presenceVillages(worlds, wid).map(presenceVillageId).find((x): x is number => !!x) ?? null })),
    [worlds, skip],
  );
  const queries = useQueries({
    queries: targets.map((t) => ({
      queryKey: keys.village(t.wid, t.vid ?? 0),
      queryFn: () => api.village.get({ wid: t.wid, vid: t.vid! }),
      enabled: !!t.vid,
      staleTime: 20_000,
      refetchInterval: 60_000,
      refetchIntervalInBackground: false,
    })),
  });
  const sig = queries.map((q) => q.dataUpdatedAt).join(",");
  return useMemo(() => {
    const m = new Map<number, WorldAttack>();
    targets.forEach((t, i) => {
      const v = queries[i]?.data;
      if (v) m.set(t.wid, worldAttack(ownVillages(v, uid)));
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targets, sig, uid]);
}
